// store/modules/apiHandler.ts
import { MutationTree, ActionTree, GetterTree } from "vuex";

//@ts-ignore
import { RootState } from "vuefront-store";

export interface State {
  apiData: Record<string, unknown>;
  apiError: { [key: string]: string | null };
  isApiLoading: { [key: string]: boolean };
}

export const state = (): State => ({
  apiData: {},
  apiError: {},
  isApiLoading: {},
});

export const getters: GetterTree<State, RootState> = {
  getApiData: (state) => (key: string) => {
    return state.apiData[key] || null;
  },
  getApiError: (state) => (key: string) => {
    return state.apiError[key] || null;
  },
  getIsLoading: (state) => (key: string) => {
    return state.isApiLoading[key] || false;
  },
};

export const mutations: MutationTree<State> = {
  setApiData(state, { key, data }) {
    state.apiData[key] = data;
  },
  setApiError(state, { key, error }) {
    state.apiError[key] = error;
  },
  setIsApiLoading(state, { key, isLoading }) {
    state.isApiLoading[key] = isLoading;
  },
};

// Prevents duplicate HTTP requests. Keyed by the actual request
// (method + URL + body + headers), NOT by the store key: the same endpoint
// is dispatched under different keys by different components (e.g. GET
// api/filters runs as "catalogFiltersData" from category.vue AND as
// "menuFiltersList" from the header menu — previously two identical ~22KB
// requests on every category load). Each caller awaits the shared fetch and
// commits the response under its own key.
// Client-only by construction (see the isClient guard below): SSR renders
// must stay per-request, so module-level promise sharing is never used there.
const inFlightRequests = new Map<string, Promise<any>>();
// Completed public responses must also be shared between sequential owners.
// Scope memory to one store, and retain serialized data: filter controls mutate
// their local copy and must not modify the header's response or cached snapshot.
const completedRequests = new WeakMap<
  State,
  Map<string, { created: number; json: string }>
>();

// --- Optional sessionStorage persistence -----------------------------------
// Static catalog-structure responses (menu categories, shapes, colors,
// features, filter groups) are identical on every page load but were
// refetched on each full reload / new tab-restore. Callers opt in with
// `persistTtlMs`; entries older than the TTL are refetched. Client-only and
// quota-guarded — persistence failures silently fall back to plain fetching.
const PERSIST_PREFIX = "vf_api_v1_";

// Shared TTL for static catalog-structure data (menu, shapes, colors,
// features, filter groups). Mirrors the SSR layout query cache (5-10 min).
export const STATIC_API_TTL_MS = 10 * 60 * 1000;

const readPersisted = (key: string, ttlMs: number): unknown | null => {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(PERSIST_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.created || Date.now() - parsed.created > ttlMs) {
      sessionStorage.removeItem(PERSIST_PREFIX + key);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
};

const writePersisted = (key: string, data: unknown) => {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(
      PERSIST_PREFIX + key,
      JSON.stringify({ data, created: Date.now() })
    );
  } catch {
    // Quota exceeded / storage disabled — non-critical.
    try {
      sessionStorage.removeItem(PERSIST_PREFIX + key);
    } catch {
      /* ignore */
    }
  }
};

export const actions: ActionTree<State, RootState> = {
  // FOR REST API
  fetchApi: async (
    { commit, state },
    {
      key,
      urlSuffix = "",
      method = "GET",
      params = {},
      headers = {},
      enableDataTrackKey = true,
      persistTtlMs,
      onError,
    }
  ) => {
    if (enableDataTrackKey && state.apiData[key]) {
      // If data is already available, prevent API call
      return;
    }

    // Local browsers use the same-origin Vite REST proxy, while Node SSR must
    // use an absolute upstream URL because fetch cannot resolve `/api/rest`.
    // The fallback keeps isolated unit callers working when no SSR define was
    // injected by a Vite server build.
    const baseUrl = import.meta.env.SSR
      ? import.meta.env.VITE_SSR_REST_API_URL ||
        import.meta.env.VITE_REST_API_URL
      : import.meta.env.VITE_REST_API_URL;
    let fullUrl = `${baseUrl}${urlSuffix}`;

    // Include parameters for GET request
    if (method === "GET" && Object.keys(params).length) {
      const urlParams = new URLSearchParams(
        Object.fromEntries(
          Object.entries(params).sort(([a], [b]) => a.localeCompare(b))
        ) as Record<string, string>
      ).toString();
      fullUrl = `${fullUrl}?${urlParams}`;
    }

    // Include body for POST/PUT requests
    let body: string | undefined;
    if (["POST", "PUT"].includes(method) && params) {
      body = JSON.stringify(params);
    }

    const requestKey = `${method} ${fullUrl} ${body || ""} ${JSON.stringify(
      headers
    )}`;
    const isClient = typeof window !== "undefined";
    const cacheable = isClient && method === "GET" && persistTtlMs > 0;
    const storageKey = `request_v2_${requestKey}`;
    let completed = completedRequests.get(state);
    if (cacheable && !completed) {
      completed = new Map();
      completedRequests.set(state, completed);
    }
    if (cacheable) {
      const cached = completed?.get(requestKey);
      if (!enableDataTrackKey) {
        completed?.delete(requestKey);
        try {
          sessionStorage.removeItem(PERSIST_PREFIX + storageKey);
        } catch {
          /* storage may be disabled */
        }
      } else {
        const data =
          cached && Date.now() - cached.created <= persistTtlMs
            ? JSON.parse(cached.json)
            : readPersisted(storageKey, persistTtlMs);
        if (data !== null) {
          commit("setApiData", { key, data });
          commit("setApiError", { key, error: null });
          return;
        }
        completed?.delete(requestKey);
      }
    }

    const doFetch = async (): Promise<any> => {
      const requestOptions: RequestInit = {
        method,
        headers: new Headers(headers),
        redirect: "follow",
      };
      if (body !== undefined) {
        requestOptions.body = body;
      }

      const response = await fetch(fullUrl, requestOptions);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    };

    // If an identical HTTP request is already in-flight, piggy-back on it
    // instead of firing a second one — even when it was dispatched under a
    // different store key.
    let promise = isClient ? inFlightRequests.get(requestKey) : undefined;
    if (!promise) {
      promise = doFetch();
      if (isClient) {
        inFlightRequests.set(requestKey, promise);
        const cleanup = () => {
          if (inFlightRequests.get(requestKey) === promise) {
            inFlightRequests.delete(requestKey);
          }
        };
        // Swallow the rejection on this bookkeeping chain only — every
        // awaiting dispatch still receives and handles the error below.
        promise.then(cleanup, cleanup);
      }
    }

    commit("setIsApiLoading", { key, isLoading: true });

    try {
      const data = await promise;
      if (cacheable) {
        const json = JSON.stringify(data);
        // Bound memory even if future callers opt in with many distinct URLs.
        if (completed!.size >= 32)
          completed!.delete(completed!.keys().next().value);
        completed!.set(requestKey, { created: Date.now(), json });
        writePersisted(storageKey, data);
        commit("setApiData", { key, data: JSON.parse(json) });
      } else {
        commit("setApiData", { key, data });
      }
      commit("setApiError", { key, error: null });
    } catch (error: any) {
      commit("setApiError", { key, error: error.toString() });
      console.error(`Error in ${method} request:`, error);
      if (typeof onError === "function") {
        onError(error.toString());
      }
    } finally {
      commit("setIsApiLoading", { key, isLoading: false });
    }
  },

  // FOR GRAPHQL API
  async fetchGraphQL(
    { commit, state },
    {
      key,
      query,
      variables = {},
      persistTtlMs,
      onError,
    }: {
      key: string;
      query: any;
      variables?: { [key: string]: any };
      persistTtlMs?: number;
      onError?: (error: string) => void;
    }
  ) {
    // `state` must come from the action context (per-store instance). It
    // previously resolved to the module-level export — one of the causes of
    // cross-request state sharing in SSR.
    if (state.apiData[key]) {
      // If data is already available, prevent API call
      return;
    }

    // Serve responses persisted by a previous page load (opt-in via
    // persistTtlMs) without hitting the network.
    if (persistTtlMs) {
      const persisted = readPersisted(key, persistTtlMs);
      if (persisted !== null) {
        commit("setApiData", { key, data: persisted });
        commit("setApiError", { key, error: null });
        return;
      }
    }

    commit("setIsApiLoading", { key, isLoading: true });

    try {
      // Perform the GraphQL query/mutation using Apollo
      const { data } = await this.$vfapollo.query(query);

      commit("setApiData", { key, data });
      commit("setApiError", { key, error: null });
      if (persistTtlMs) {
        writePersisted(key, data);
      }
    } catch (error: any) {
      commit("setApiError", { key, error: error.toString() });
      console.error("Error in GraphQL request:", error);
      if (typeof onError === "function") {
        onError(error.toString());
      }
    } finally {
      commit("setIsApiLoading", { key, isLoading: false });
    }
  },
};
